import classNames from "@/utils/classNames";
import { InputHTMLAttributes, ReactNode, useState } from "react";

type CheckboxProps = {
    label?: ReactNode;
    onChange?: (e: boolean) => void;
};

export default function Checkbox({
    className = "",
    label,
    ...props
}: Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> & CheckboxProps) {
    const [isChecked, setIsChecked] = useState(props.checked);
    const [previousChecked, setPreviousChecked] = useState(props.checked);

    // Keep the internal state in sync when the controlled `checked` prop changes
    // (e.g. a parent resets the filter programmatically). Adjusting state during
    // render avoids an effect and avoids remounting, so focus is never lost.
    if (props.checked !== previousChecked) {
        setPreviousChecked(props.checked);
        setIsChecked(props.checked);
    }

    const handleToggle = () => {
        if (props.disabled) return;
        setIsChecked(!isChecked);
        props.onChange?.(!isChecked);
    };

    return (
        <div
            className={classNames(
                "flex",
                props.disabled ? "cursor-not-allowed" : "cursor-pointer",
            )}
            onClick={handleToggle}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleToggle();
                }
            }}
            role="checkbox"
            aria-checked={isChecked}
            tabIndex={props.disabled ? -1 : 0}
        >
            <input
                {...props}
                type="checkbox"
                data-testid="select-checkbox"
                className={classNames(
                    "h-5 w-5 cursor-pointer accent-brand-red rounded-sm text-brand-red focus:ring-transparent disabled:cursor-not-allowed disabled:bg-brand-lightGray",
                    className,
                )}
                checked={isChecked}
                onChange={() => {}}
            />
            {label && (
                <span
                    className={classNames(
                        "-mt-[2.5px] ms-2",
                        props.disabled
                            ? "cursor-not-allowed"
                            : "cursor-pointer",
                    )}
                >
                    {label}
                </span>
            )}
        </div>
    );
}
